import { Box, SimpleGrid, Text } from "@chakra-ui/react";
import React, { type ReactNode } from "react";
import Balancer from "react-wrap-balancer";
import { GradientCard } from "ui/cards";
import { ArrowLink } from "ui/link";

type Props = {
  variant: React.ComponentProps<typeof GradientCard>["variant"];
  eyebrow?: string;
  title: string;
  description: string;
  cta?: {
    href: string;
    text: string;
  };
  featureNode: ReactNode;
};

/**
 * A full-width card with a gradient background.
 * Used to display copy about a feature alongside a graphic element.
 */
export function FullWidthFeatureCard({ eyebrow, title, description, cta, featureNode, ...props }: Props) {
  return (
    <GradientCard overflow="hidden" startColorStop="-60%" endColorStop="140%" angle="230deg" {...props}>
      <SimpleGrid columns={{ base: 1, md: 2 }} maxW={{ base: "md", md: "100%" }}>
        <Box alignSelf="center" px={{ base: 6, md: 12, xl: 14 }} py={{ base: 10, lg: 12 }}>
          {eyebrow && (
            <Text fontSize={{ base: "sm", lg: "md" }} textStyle="uppercase" mb={5}>
              {eyebrow}
            </Text>
          )}

          <Text as="h3" fontSize={{ base: "xl", md: "2xl", lg: "3xl", xl: "4xl" }} fontWeight="semibold">
            <Balancer>{title}</Balancer>
          </Text>

          <Text fontSize={{ base: "md", lg: "lg" }} mt={5}>
            {description}
          </Text>

          {cta && (
            <ArrowLink variant="dark" fontSize={{ base: "md", lg: "lg" }} href={cta.href} mt="9">
              {cta.text}
            </ArrowLink>
          )}
        </Box>

        <SimpleGrid w="100%" h="100%" pt={{ sm: 4, md: 10 }} pb={{ base: 8, sm: 10 }} alignItems="center">
          {featureNode}
        </SimpleGrid>
      </SimpleGrid>
    </GradientCard>
  );
}
